import * as XLSX from "xlsx";

/**
 * Müzakere toplu yükleme: örnek Excel ve katı format kontrolü.
 *
 * Format (ilk sayfa, ilk satır başlık):
 *   Başlık   | Tarih                       | Açıklama
 *   zorunlu  | zorunlu, Excel tarihi ya da | isteğe bağlı
 *   ≤100     | GG.AA.YYYY (YYYY-AA-GG de)  | ≤500
 * Başlık satırı birebir aynı olmalı; format dışı dosya hiç kabul edilmez.
 * Sunucu (bulk-create) aynı kuralları ayrıca uygular.
 */

export const MUZAKERE_HEADERS = ["Başlık", "Tarih", "Açıklama"];
export const MUZAKERE_MAX_ROWS = 200;
export const MUZAKERE_TEMPLATE_NAME = "muzakere-ornek.xlsx";
const TITLE_MAX = 100;
const DESCRIPTION_MAX = 500;

const pad = (n) => String(n).padStart(2, "0");
const toYmd = (y, m, d) => `${y}-${pad(m)}-${pad(d)}`;

function isRealDate(y, m, d) {
  if (!y || !m || !d) return false;
  const dt = new Date(y, m - 1, d);
  return dt.getFullYear() === y && dt.getMonth() === m - 1 && dt.getDate() === d;
}

/** Hücredeki tarihi "YYYY-MM-DD"ye çevirir; geçersizse null. */
export function parseCellDate(value) {
  if (typeof value === "number" && Number.isFinite(value)) {
    // Excel tarih seri numarası (saat dilimi kaymadan gün olarak çözülür)
    const p = XLSX.SSF.parse_date_code(value);
    return p && isRealDate(p.y, p.m, p.d) ? toYmd(p.y, p.m, p.d) : null;
  }
  const text = String(value ?? "").trim();
  let m = text.match(/^(\d{1,2})[./](\d{1,2})[./](\d{4})$/);
  if (m) {
    const [d, mo, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return isRealDate(y, mo, d) ? toYmd(y, mo, d) : null;
  }
  m = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) {
    const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
    return isRealDate(y, mo, d) ? toYmd(y, mo, d) : null;
  }
  return null;
}

/** Bugünün bulunduğu haftanın Pazartesi'si ("YYYY-MM-DD"). */
export function currentWeekStart(now = new Date()) {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = d.getDay(); // 0 = Pazar
  d.setDate(d.getDate() - (dow === 0 ? 6 : dow - 1));
  return toYmd(d.getFullYear(), d.getMonth() + 1, d.getDate());
}

/**
 * Excel dosyasının içeriğini (ArrayBuffer) doğrular.
 * Dönüş: { rows: [{ title, date, description }], errors: string[] }.
 * errors doluysa rows kullanılmamalı (ya hep ya hiç).
 */
export function parseMuzakereWorkbook(data, { now = new Date() } = {}) {
  let workbook;
  try {
    workbook = XLSX.read(data, { type: "array" });
  } catch {
    return { rows: [], errors: ["Dosya okunamadı. Lütfen örnek Excel dosyasını kullanın."] };
  }
  const sheetName = workbook.SheetNames[0];
  const sheet = sheetName ? workbook.Sheets[sheetName] : null;
  if (!sheet) return { rows: [], errors: ["Dosyada sayfa bulunamadı."] };

  const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  const header = (matrix[0] || []).map((h) => String(h ?? "").trim());
  const headerOk =
    MUZAKERE_HEADERS.every((h, i) => header[i] === h) &&
    header.slice(MUZAKERE_HEADERS.length).every((h) => h === "");
  if (!headerOk) {
    return {
      rows: [],
      errors: [
        `İlk satır tam olarak "${MUZAKERE_HEADERS.join(" | ")}" olmalı. Lütfen örnek Excel dosyasını kullanın.`,
      ],
    };
  }

  const minDate = currentWeekStart(now);
  const errors = [];
  const rows = [];
  matrix.slice(1).forEach((cells, index) => {
    const line = index + 2;
    const [rawTitle, rawDate, rawDescription] = cells;
    const extra = cells.slice(MUZAKERE_HEADERS.length).some((c) => String(c ?? "").trim() !== "");
    const title = String(rawTitle ?? "").trim();
    const description = String(rawDescription ?? "").trim();
    const empty = !title && String(rawDate ?? "").trim() === "" && !description && !extra;
    if (empty) return; // tamamen boş satırlar atlanır

    if (extra) errors.push(`${line}. satır: Yalnızca ilk üç sütun doldurulabilir`);
    if (!title) errors.push(`${line}. satır: Başlık boş olamaz`);
    else if (title.length > TITLE_MAX)
      errors.push(`${line}. satır: Başlık ${TITLE_MAX} karakterden uzun olamaz`);
    if (description.length > DESCRIPTION_MAX)
      errors.push(`${line}. satır: Açıklama ${DESCRIPTION_MAX} karakterden uzun olamaz`);

    const date = parseCellDate(rawDate);
    if (!date) errors.push(`${line}. satır: Tarih GG.AA.YYYY biçiminde olmalı`);
    else if (date < minDate)
      errors.push(`${line}. satır: Geçmiş haftalara müzakere eklenemez`);

    rows.push({ title, date, description });
  });

  if (rows.length === 0 && errors.length === 0) {
    errors.push("Dosyada müzakere satırı yok.");
  }
  if (rows.length > MUZAKERE_MAX_ROWS) {
    errors.push(`Tek seferde en fazla ${MUZAKERE_MAX_ROWS} müzakere yüklenebilir.`);
  }
  return { rows: errors.length ? [] : rows, errors };
}

/** Örnek dosyayı oluşturur (indirme için workbook döner). */
export function buildMuzakereTemplate(now = new Date()) {
  const monday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const dow = monday.getDay();
  monday.setDate(monday.getDate() + (dow === 0 ? 1 : 8 - dow)); // gelecek Pazartesi
  const fmt = (d) => `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
  const plus = (days) => {
    const d = new Date(monday);
    d.setDate(d.getDate() + days);
    return fmt(d);
  };

  const sheet = XLSX.utils.aoa_to_sheet([
    MUZAKERE_HEADERS,
    ["Lem'alar 1. Lem'a", plus(0), "Akşam namazından sonra"],
    ["Sözler 10. Söz", plus(3), ""],
  ]);
  sheet["!cols"] = [{ wch: 40 }, { wch: 14 }, { wch: 50 }];

  const guide = XLSX.utils.aoa_to_sheet([
    ["Nasıl doldurulur?"],
    ["Müzakereleri ilk sayfaya (Müzakereler) yazın; ilk satırdaki başlıkları değiştirmeyin."],
    ["Başlık: zorunlu, en fazla 100 karakter."],
    ["Tarih: zorunlu, GG.AA.YYYY biçiminde (örn. 05.10.2026). Bu haftadan önceki tarihler kabul edilmez."],
    ["Açıklama: isteğe bağlı, en fazla 500 karakter."],
    [`Tek dosyada en fazla ${MUZAKERE_MAX_ROWS} satır. Örnek satırları silip kendi müzakerelerinizi yazın.`],
    ["Tek bir satır bile hatalıysa dosyanın tamamı reddedilir; hatalar satır numarasıyla gösterilir."],
  ]);
  guide["!cols"] = [{ wch: 100 }];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, "Müzakereler");
  XLSX.utils.book_append_sheet(workbook, guide, "Nasıl Doldurulur");
  return workbook;
}

export function downloadMuzakereTemplate() {
  XLSX.writeFile(buildMuzakereTemplate(), MUZAKERE_TEMPLATE_NAME);
}
